import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../themes/typography';
import { UNITS_RAMP } from '../../pages/Machines/hooks/useHeatmapColors';

const rgb = (c) => `rgb(${c.join(',')})`;
// The heat map's darkest step for whole months; the current one, still counting, a step lighter
const FULL = rgb(UNITS_RAMP[UNITS_RAMP.length - 1][1]);
const SO_FAR = rgb(UNITS_RAMP[UNITS_RAMP.length - 2][1]);
const PLOT_HEIGHT = 44;

// A month the product was not in the lane: no sale from it and never seen in it
const isAbsent = (series, days, i) => days != null && !(days[i] > 0) && !(series[i] > 0);
/** Whether the bars show a "—" anywhere: only then does their legend need saying. */
export const hasAbsentMonth = (series, days) => (series || []).some((_, i) => isAbsent(series, days, i));

/**
 * A lane's units month by month (`months` are 'YYYY-MM-01', the last one the current month so far): one thin bar per
 * month from a common baseline, the month under it and its units above it. With `days` (the days its product was in
 * the lane) and `snapshotDays` (the machine's), a month it was not there — no sale from the lane and never seen in
 * it — has no bar and a "—", and a month it was seen in under half of the machine's snapshots — like the current
 * one, still counting — a lighter bar, so a low number is not read as a bad month. A sale beats the snapshots: they
 * miss some lanes some days, so a month with sales always counts as there. Bars are drawn against `max` (by default
 * the series' own best month): every lane of a machine gets the same one.
 */
const MonthlyUnitsBars = ({ months, series, days, snapshotDays, locale, max: scaleMax }) => {
  const max = Math.max(1, scaleMax ?? 0, ...series);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px' }}>
      {series.map((units, i) => {
        const absent = isAbsent(series, days, i);
        const partial = i === series.length - 1
          || (days != null && snapshotDays != null && days[i] > 0 && days[i] < snapshotDays[i] / 2);
        return (
          <Box key={months[i]} sx={{ width: 30, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '9.5px', lineHeight: 1.2, fontWeight: 600, color: absent ? colors.text.muted : colors.text.primary }}>
              {absent ? '—' : Math.round(units)}
            </Typography>
            <Box sx={{ height: PLOT_HEIGHT, width: 14, display: 'flex', alignItems: 'flex-end' }}>
              {!absent && (
                <Box sx={{
                  width: '100%',
                  height: units > 0 ? Math.max(2, Math.round((units / max) * PLOT_HEIGHT)) : 0,
                  borderRadius: '4px 4px 0 0',
                  backgroundColor: partial ? SO_FAR : FULL,
                }} />
              )}
            </Box>
            <Box sx={{ width: '100%', borderTop: `1px ${absent ? 'dashed' : 'solid'} ${colors.border.light}` }} />
            <Typography sx={{ fontSize: '9.5px', lineHeight: 1.4, color: absent ? colors.text.muted : colors.text.secondary }}>
              {new Date(`${months[i]}T12:00:00`).toLocaleDateString(locale, { month: 'short' }).replace('.', '')}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default MonthlyUnitsBars;
